/**
 * OKS-391 — Compact number formatting for the TikTok profile-strip
 * stats (Following / Followers / Likes), mirroring TikTok's own profile
 * header. Large counts collapse to a K / M / B suffix.
 *
 *   999            → "999"
 *   1_000          → "1K"        (trailing .0 trimmed)
 *   1_200          → "1.2K"
 *   8_450          → "8.5K"      (rounded to 1 decimal)
 *   10_500         → "10.5K"
 *   214_900        → "214.9K"
 *   1_200_000      → "1.2M"
 *   3_400_000_000  → "3.4B"
 *
 * Deliberately LOCALE-INDEPENDENT: TikTok renders the same "." decimal
 * style in every locale, so the strip matches that universally (a `fr`
 * creator still sees "8.5K", not "8,5K"). Values below 1_000 never need
 * grouping, so no locale-aware separator is required at any tier.
 *
 * `null` / `undefined` / `NaN` → the em-dash placeholder "—" (PRD OQ-3 /
 * UX Spec §3 edge case): a freshly-linked account with no captured
 * metrics snapshot still renders the strip — only the values degrade.
 *
 * Pure function; no external dependencies.
 */

/** The em-dash placeholder rendered when a stat value is absent. */
export const STAT_EMPTY = "—"

interface Tier {
  threshold: number
  suffix: string
}

// Ordered high → low so the first match wins.
const TIERS: Tier[] = [
  { threshold: 1_000_000_000, suffix: "B" },
  { threshold: 1_000_000, suffix: "M" },
  { threshold: 1_000, suffix: "K" },
]

/**
 * @param value A non-negative integer count, or `null`/`undefined` when
 *              no metrics snapshot is available.
 * @return      Compact string (`"8.5K"`) or `STAT_EMPTY` ("—").
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return STAT_EMPTY
  }

  if (value < 1_000) {
    return String(value)
  }

  const tier = TIERS.find((t) => value >= t.threshold)!
  // Round to one decimal, then trim a trailing ".0":
  // 1_000 → "1K", 8_450 → "8.5K".
  const rounded = Math.round((value / tier.threshold) * 10) / 10
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)

  return `${text}${tier.suffix}`
}
